import { List, Map } from 'immutable';

import { makeValue } from './shapeAttributes';

import { ATTRIBUTE_DEFAULTS, DEFAULT_OPTION, DEFAULT_VALUES, DEFAULT_VARIANT, ENABLED, ERROR, NEW, OVERSET, REQUIRED, SIZE_UNIT, WEIGHT_UNIT } from '../../constants/product';
import { ALL, COMBINATIONS, INFLUENCE, RETAIN, VARIANTS } from '../../constants/variations';
import { EBAY, ETSY, FAIRE, SHOPIFY } from '../../constants/channels';
import { ATTRIBUTE } from '../../constants/attributes';
import { EMPTY } from '../../constants';
import { FORM } from '../../constants/forms';

export function newDetails({ channel }) {
  switch (channel) {
    case EBAY: {
      return Map({
        [ATTRIBUTE.CONDITION]: DEFAULT_VALUES.NULL.set(ATTRIBUTE.DESCRIPTION, ''),
        [ATTRIBUTE.ITEM_CATEGORY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.ITEM_SPECIFICS]: DEFAULT_VALUES.EMPTY_MAP,
        [ATTRIBUTE.PRODUCT_DETAILS]: DEFAULT_VALUES.EMPTY_MAP,
        [ATTRIBUTE.STORE_CATEGORY]: DEFAULT_VALUES.NULL,
      });
    }

    case ETSY: {
      return Map({
        [ATTRIBUTE.CATEGORY]: DEFAULT_VALUES.ZERO,
        [ATTRIBUTE.DIGITAL]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.IS_SUPPLY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.MATERIALS]: DEFAULT_VALUES.EMPTY_ORDERED_MAP,
        [ATTRIBUTE.PRODUCTION_PARTNERS]: DEFAULT_VALUES.EMPTY_LIST,
        [ATTRIBUTE.SECTION]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.SHOULD_AUTO_RENEW]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.TAXONOMY_ATTRIBUTES]: EMPTY.MAP,
        [ATTRIBUTE.WHEN_MADE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.WHO_MADE]: DEFAULT_VALUES.NULL,
      });
    }

    case SHOPIFY: {
      return Map({
        [ATTRIBUTE.CATEGORY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.COLLECTIONS]: DEFAULT_VALUES.EMPTY_LIST,
        [ATTRIBUTE.PRODUCT_TYPE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.VENDOR]: DEFAULT_VALUES.NULL,
      });
    }

    case FAIRE: {
      return Map({
        [ATTRIBUTE.CATEGORY]: DEFAULT_VALUES.NULL,
      });
    }

    default: {
      return EMPTY.MAP;
    }
  }
}

export function newInventory({ asProfile, channel }) {
  switch (channel) {
    case EBAY: {
      return Map({
        [ATTRIBUTE.PRICE]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.QUANTITY]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.SKU]: DEFAULT_VALUES.EMPTY_STRING.set(REQUIRED, false),
      });
    }

    case ETSY: {
      return Map({
        [ATTRIBUTE.PRICE]: asProfile ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ZERO_CENT,
        [ATTRIBUTE.QUANTITY]: asProfile ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ONE,
        [ATTRIBUTE.SKU]: DEFAULT_VALUES.EMPTY_STRING,
      });
    }

    case FAIRE: {
      return Map({
        [ATTRIBUTE.CONTINUE_SELLING]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.DEADLINE_DATE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.DISTANCE_UNIT]: DEFAULT_VALUES.DISTANCE_UNIT,
        [ATTRIBUTE.END_DATE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.GTIN]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.HEIGHT]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.INTERNATIONAL_PRICING]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.KEEP_ACTIVE]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.LENGTH]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.MASS_UNIT]: DEFAULT_VALUES.MASS_UNIT,
        [ATTRIBUTE.PREORDER]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.QUANTITY]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.RESTOCK_DATE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.RETAIL_PRICE]: DEFAULT_VALUES.EMPTY_INTERNATIONAL_PRICES,
        [ATTRIBUTE.SKU]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.START_DATE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.TARIFF_CODE]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.WEIGHT]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.WHOLESALE_PRICE]: DEFAULT_VALUES.EMPTY_INTERNATIONAL_PRICES,
        [ATTRIBUTE.WIDTH]: DEFAULT_VALUES.EMPTY_STRING,
      });
    }

    case SHOPIFY: {
      const DEFAULT_VALUE = Map({
        options: List([DEFAULT_OPTION[SHOPIFY]]),
        variants: List([DEFAULT_VARIANT[SHOPIFY]]),
      });

      return Map({
        [ATTRIBUTE.BARCODE]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.CAP]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.CHARGE_TAX]: DEFAULT_VALUES.TRUE,
        [ATTRIBUTE.CONTINUE_SELLING]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.COUNTRY_CODE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.CPI]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.DEFAULT_VALUE]: DEFAULT_VALUE,
        [ATTRIBUTE.HS_CODE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.PHYSICAL]: DEFAULT_VALUES.TRUE,
        [ATTRIBUTE.PRICE]: asProfile ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ZERO_CENT,
        [ATTRIBUTE.QUANTITY]: asProfile ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ZERO,
        [ATTRIBUTE.SKU]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.TRACK_QUANTITY]: DEFAULT_VALUES.TRUE,
        [ATTRIBUTE.UNIT]: DEFAULT_VALUES.UNIT,
        [ATTRIBUTE.WEIGHT]: asProfile ? DEFAULT_VALUES.EMPTY_STRING : DEFAULT_VALUES.ZERO,
      });
    }

    default: {
      return EMPTY.MAP;
    }
  }
}

export function newPersonalization() {
  return Map({
    [ENABLED]: false,
    [ATTRIBUTE.CHAR_COUNT_MAX]: DEFAULT_VALUES.NULL,
    [ATTRIBUTE.INSTRUCTIONS]: DEFAULT_VALUES.EMPTY_STRING,
    [REQUIRED]: false,
  });
}

export function newShipping({ channel }) {
  switch (channel) {
    case EBAY: {
      return Map({
        [ATTRIBUTE.COUNTRY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.DEPTH]: DEFAULT_VALUES.EMPTY_STRING.set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
        [ATTRIBUTE.IRREGULAR]: DEFAULT_VALUES.FALSE,
        [ATTRIBUTE.LENGTH]: DEFAULT_VALUES.EMPTY_STRING.set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
        [ATTRIBUTE.LOCATION]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.PAYMENT_POLICY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.POSTAL_CODE]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.RETURN_POLICY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.SHIPPING_POLICY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.WEIGHT_MAJOR]: DEFAULT_VALUES.EMPTY_STRING.set(ATTRIBUTE.UNIT, WEIGHT_UNIT.LB),
        [ATTRIBUTE.WEIGHT_MINOR]: DEFAULT_VALUES.EMPTY_STRING.set(ATTRIBUTE.UNIT, WEIGHT_UNIT.OZ),
        [ATTRIBUTE.WIDTH]: DEFAULT_VALUES.EMPTY_STRING.set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
      });
    }

    case ETSY: {
      return Map({
        [ATTRIBUTE.HEIGHT]: DEFAULT_VALUES.NULL.set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
        [ATTRIBUTE.LENGTH]: DEFAULT_VALUES.NULL.set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
        [ATTRIBUTE.PROCESSING_PROFILE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.PROFILE_ID]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.PROFILE_TYPE]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.RETURN_POLICY]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.WEIGHT]: DEFAULT_VALUES.NULL,
        [ATTRIBUTE.WIDTH]: DEFAULT_VALUES.NULL.set(ATTRIBUTE.UNIT, SIZE_UNIT.IN),
      });
    }

    case FAIRE: {
      return Map({
        [ATTRIBUTE.CASE_SIZE]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.MINIMUM_ORDER_QUANTITY]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.PER_STYLE_MINIMUM_ORDER_QUANTITY]: DEFAULT_VALUES.EMPTY_STRING,
        [ATTRIBUTE.SELLING_METHOD]: DEFAULT_VALUES.SELLING_METHOD,
      });
    }

    default: {
      return undefined;
    }
  }
}

export function newVariations({ asProfile, channel }) {
  let variations = Map({
    [COMBINATIONS]: EMPTY.LIST,
    [VARIANTS]: EMPTY.LIST,
    [ERROR]: EMPTY.MAP,
  });

  if (asProfile) {
    variations = variations.set(RETAIN, EMPTY.MAP);
  }

  switch (channel) {
    case EBAY: {
      const required = Map({ [ATTRIBUTE.SKU]: false });
      return variations.set(INFLUENCE, EMPTY.MAP).set(REQUIRED, required);
    }

    case ETSY: {
      const influence = Map({
        [ALL]: false,
        [ATTRIBUTE.PRICE]: EMPTY.MAP,
        [ATTRIBUTE.PROCESSING_PROFILE]: EMPTY.MAP,
        [ATTRIBUTE.QUANTITY]: EMPTY.MAP,
        [ATTRIBUTE.SKU]: EMPTY.MAP,
      });

      return variations.set(INFLUENCE, influence);
    }

    case FAIRE: {
      const influence = Map({
        [ATTRIBUTE.CONTINUE_SELLING]: false,
        [ATTRIBUTE.INTERNATIONAL_PRICING]: false,
        [ATTRIBUTE.KEEP_ACTIVE]: false,
      });

      return variations.set(INFLUENCE, influence);
    }

    default: {
      return variations;
    }
  }
}

export function newProduct({ asProfile, channel }) {
  const product = Map({ channel, productId: NEW });

  switch (channel) {
    case EBAY: {
      const description = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.DESCRIPTION]);
      const details = newDetails({ channel });
      const inventory = newInventory({ asProfile, channel });
      const photos = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.PHOTOS]);
      const shipping = newShipping({ channel });
      const title = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TITLE]);
      const variations = newVariations({ asProfile, channel });
      const videos = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.VIDEOS]);

      return product
        .set(FORM.DESCRIPTION, description)
        .set(FORM.DETAILS, details)
        .set(FORM.INVENTORY, inventory)
        .set(FORM.PHOTOS, photos)
        .set(FORM.SHIPPING, shipping)
        .set(FORM.TITLE, title)
        .set(FORM.VARIATIONS, variations)
        .set(FORM.VIDEOS, videos);
    }

    case ETSY: {
      const description = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.DESCRIPTION]);
      const details = newDetails({ channel });
      const inventory = newInventory({ asProfile, channel });
      const personalization = newPersonalization();
      const photos = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.PHOTOS]);
      const shipping = newShipping({ channel });
      const tags = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TAGS]);
      const title = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TITLE]);
      const variations = newVariations({ asProfile, channel });
      const videos = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.VIDEOS]);

      return product
        .set(FORM.DESCRIPTION, description)
        .set(FORM.DETAILS, details)
        .set(FORM.INVENTORY, inventory)
        .set(FORM.PERSONALIZATION, personalization)
        .set(FORM.PHOTOS, photos)
        .set(FORM.SHIPPING, shipping)
        .set(FORM.TAGS, tags)
        .set(FORM.TITLE, title)
        .set(FORM.VARIATIONS, variations)
        .set(FORM.VIDEOS, videos);
    }

    case FAIRE: {
      const description = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.DESCRIPTION]);
      const details = newDetails({ channel });
      const inventory = newInventory({ asProfile, channel });
      const photos = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.PHOTOS]);
      const shipping = newShipping({ channel });
      const title = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TITLE]);
      const variations = newVariations({ asProfile, channel });

      return product
        .set(FORM.DESCRIPTION, description)
        .set(FORM.DETAILS, details)
        .set(FORM.INVENTORY, inventory)
        .set(FORM.PHOTOS, photos)
        .set(FORM.SHIPPING, shipping)
        .set(FORM.TITLE, title)
        .set(FORM.VARIATIONS, variations);
    }

    case SHOPIFY: {
      const description = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.DESCRIPTION]);
      const details = newDetails({ channel });
      const inventory = newInventory({ asProfile, channel });
      const photos = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.PHOTOS]);
      const tags = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TAGS]);
      const title = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.TITLE]);
      const variations = newVariations({ asProfile, channel });
      const videos = makeValue(ATTRIBUTE_DEFAULTS[channel][ATTRIBUTE.VIDEOS]);
      const seo = Map({
        [ATTRIBUTE.META_DESCRIPTION]: DEFAULT_VALUES.EMPTY_STRING.set(OVERSET, true),
        [ATTRIBUTE.PAGE_TITLE]: DEFAULT_VALUES.EMPTY_STRING.set(OVERSET, true),
      });

      return product
        .set(FORM.DESCRIPTION, description)
        .set(FORM.DETAILS, details)
        .set(FORM.INVENTORY, inventory)
        .set(FORM.PHOTOS, photos)
        .set(FORM.SEO, seo)
        .set(FORM.TAGS, tags)
        .set(FORM.TITLE, title)
        .set(FORM.VARIATIONS, variations)
        .set(FORM.VIDEOS, videos);
    }

    default: {
      break;
    }
  }

  return product;
}
